/**
 * CANONICAL METRICS DEFINITION
 * Single source of truth for all metrics across the application.
 *
 * ⚠️  THIS FILE IS 100% GENERATED BY THE BACKEND (metricsregistryexport.py).
 *     DO NOT EDIT THIS FILE MANUALLY — any changes will be overwritten
 *     on the next export run. To modify metrics, update the MetricsRegistry
 *     in MongoDB or the backend export script instead.
 *
 * Each metric defines:
 * - id: Unique identifier
 * - field: Primary display name
 * - variants: Related metric representations (absolute, growth, ratio, etc.)
 * - dbField: Backend API field name (if different from field)
 * - group: Category for filtering/grouping
 * - format: Display format type
 * - proMetric: Whether this requires Pro subscription
 * - views: UI views that display this variant (compSheet, dashboard, valuation, etc.)
 * - displayPlacements: Optional view-specific grouping/ordering hints
 * - metadata: Additional display/calculation properties
 *
 * AUTO-GENERATED — do not edit manually.
 * Source: FinancialService.MetricsRegistry → metricsregistryexport.py
 */


// ============================================
// METRIC FORMAT TYPES

// ============================================

export const METRIC_FORMATS = {
  CURRENCY: 'currency',
  PERCENTAGE: 'percentage',
  NUMBER: 'number',
  MULTIPLE: 'multiple',
  MONETARY_KPI: 'monetaryKpi',
};


// ============================================
// METRIC TYPES (Flow vs Instant)

// ============================================

export const METRIC_TYPES = {
  FLOW: 'flow',  // P&L items that aggregate over time - can use TTM
  INSTANT: 'instant',  // Balance sheet/point-in-time snapshots - cannot use TTM
};


// ============================================
// METRIC GROUPS

// ============================================

export const METRIC_GROUPS = {
  TOP_LINE: 'Top-line Metrics',
  UNIT_ECONOMICS: 'Unit Economics',
  PROFITABILITY: 'Profitability Metrics',
  BUSINESS_COMPOSITION: 'Business Composition',
  EFFICIENCY: 'Efficiency Metrics',
  GROWTH: 'Growth Metrics',
  COMPANY_STATS: 'Company Statistics',
  ACCELERATION: 'Acceleration',
};


// ============================================
// METRIC VARIANT TYPES

// ============================================

export const VARIANT_TYPES = {
  ABSOLUTE: 'absolute',  // Total RPO, Revenue
  GROWTH: 'growth',  // RPO YOY%, Revenue YOY%
  TTM_GROWTH: 'ttm_growth',  // TTM Bookings YOY%
  RATIO: 'ratio',  // Revenue/Customer
  PERCENT_OF: 'percent_of',  // GM % of Sales
  MULTIPLE: 'multiple',  // EV/Sales NTM
  ACCELERATION: 'acceleration',
  TTM_ACCELERATION: 'ttm_acceleration',
};


// ============================================
// CANONICAL METRICS REGISTRY

// ============================================

export const METRICS_REGISTRY = {


  // ========== REVENUE METRICS ==========

    revenue: {
      id: 'revenue',
      field: 'Revenue',
      group: METRIC_GROUPS.TOP_LINE,
      metricType: METRIC_TYPES.FLOW,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'Revenue',
          dbField: 'revenue',
          format: METRIC_FORMATS.CURRENCY,
          proMetric: false,
          views: {
            securityAnalysis: true,
            dashboard: {
              placements: [{
  group: 'Profitability Metrics',
  section: 'Profitability Metrics',
  order: 0,
}],
            },
            model: {
              placements: [{
  group: 'Profitability Metrics',
  section: 'P&L',
  order: 0,
}],
            },
          },
          displayPlacements: [{
  view: 'dashboard',
  group: 'Profitability Metrics',
  section: 'Profitability Metrics',
  order: 0,
}, {
  view: 'model',
  group: 'Profitability Metrics',
  section: 'P&L',
  order: 0,
}],
          metadata: {
            fontWeight: 500,
            tab: 1,
            calc: ['% Growth', '$ Change'],
          },
        },
        [VARIANT_TYPES.GROWTH]: {
          field: 'Revenue YOY%',
          headerName: 'Revenue',
          sublabel: 'YoY%',
          dbField: 'revenueyoy',
          format: METRIC_FORMATS.PERCENTAGE,
          proMetric: false,
          views: {
            compSheet: true,
            dashboard: true,
            valuation: {
              axis: 'x',
            },
            trended: true,
          },
          metadata: {
            displayGraph: true,
          },
        },
        [VARIANT_TYPES.TTM_GROWTH]: {
          field: 'TTM Revenue YOY%',
          headerName: 'Revenue',
          sublabel: 'TTM YoY%',
          dbField: 'revenuettmYoy',
          format: METRIC_FORMATS.PERCENTAGE,
          proMetric: false,
          views: {
            compSheet: true,
            dashboard: true,
            valuation: {
              axis: 'x',
            },
          },
          metadata: {
            displayGraph: true,
          },
        },
        [VARIANT_TYPES.ACCELERATION]: {
          field: 'Revenue Accel',
          headerName: 'Revenue',
          sublabel: 'Accel (pp)',
          dbField: 'revenueAccel',
          group: METRIC_GROUPS.ACCELERATION,
          format: METRIC_FORMATS.PERCENTAGE,
          proMetric: false,
          views: {
            accel: true,
            compSheet: true,
            dashboard: true,
          },
        },
      },
    },

    arr: {
      id: 'arr',
      field: 'ARR',
      group: METRIC_GROUPS.TOP_LINE,
      metricType: METRIC_TYPES.INSTANT,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'ARR',
          dbField: 'arr',
          format: METRIC_FORMATS.CURRENCY,
          proMetric: false,
          views: {
            securityAnalysis: true,
          },
          metadata: {
            fontWeight: 500,
            tab: 1,
            calc: ['% Growth', '$ Change'],
          },
        },
        [VARIANT_TYPES.GROWTH]: {
          field: 'ARR YOY%',
          headerName: 'ARR',
          sublabel: 'YoY%',
          dbField: 'arryoy',
          format: METRIC_FORMATS.PERCENTAGE,
          proMetric: true,
          views: {
            compSheet: true,
            dashboard: true,
            valuation: {
              axis: 'x',
            },
          },
          metadata: {
            displayGraph: true,
          },
        },
        [VARIANT_TYPES.ACCELERATION]: {
          field: 'ARR Accel',
          headerName: 'ARR',
          sublabel: 'Accel (pp)',
          dbField: 'arrAccel',
          group: METRIC_GROUPS.ACCELERATION,
          format: METRIC_FORMATS.PERCENTAGE,
          proMetric: true,
          views: {
            accel: true,
            compSheet: true,
            dashboard: true,
          },
        },
      },
    },

    cloudArr: {
      id: 'cloudArr',
      field: 'Cloud ARR',
      group: METRIC_GROUPS.TOP_LINE,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'Cloud ARR',
          dbField: 'cloudArr',
          format: METRIC_FORMATS.CURRENCY,
          proMetric: true,
          metadata: {
            fontWeight: 400,
            tab: 2,
            calc: ['% Growth', '$ Change'],
          },
        },
        [VARIANT_TYPES.GROWTH]: {
          field: 'Cloud ARR YOY%',
          headerName: 'Cloud ARR',
          sublabel: 'YoY%',
          dbField: 'cloudArryoy',
          format: METRIC_FORMATS.PERCENTAGE,
          proMetric: true,
          views: {
            compSheet: true,
            valuation: {
              axis: 'x',
            },
          },
          metadata: {
            displayGraph: true,
          },
        },
      },
    },

    creativeArr: {
      id: 'creativeArr',
      field: 'Creative ARR',
      group: METRIC_GROUPS.BUSINESS_COMPOSITION,
      metricType: METRIC_TYPES.INSTANT,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'Creative ARR',
          dbField: 'creativeArr',
          format: METRIC_FORMATS.CURRENCY,
          proMetric: true,
          views: {
            securityAnalysis: true,
          },
        },
      },
    },

    digitalMediaArr: {
      id: 'digitalMediaArr',
      field: 'Digital Media ARR',
      group: METRIC_GROUPS.BUSINESS_COMPOSITION,
      metricType: METRIC_TYPES.INSTANT,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'Digital Media ARR',
          dbField: 'digitalMediaArr',
          format: METRIC_FORMATS.CURRENCY,
          proMetric: true,
          views: {
            securityAnalysis: true,
          },
        },
      },
    },

    documentArr: {
      id: 'documentArr',
      field: 'Document ARR',
      group: METRIC_GROUPS.BUSINESS_COMPOSITION,
      metricType: METRIC_TYPES.INSTANT,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'Document ARR',
          dbField: 'documentArr',
          format: METRIC_FORMATS.CURRENCY,
          proMetric: true,
          views: {
            securityAnalysis: true,
          },
        },
      },
    },

    cloudRevenue: {
      id: 'cloudRevenue',
      field: 'Cloud Revenue',
      group: METRIC_GROUPS.TOP_LINE,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'Cloud Revenue',
          dbField: 'cloudRevenue',
          format: METRIC_FORMATS.CURRENCY,
          proMetric: true,
          metadata: {
            fontWeight: 400,
            tab: 2,
            calc: ['% Growth', '$ Change', '% of Revenue'],
          },
        },
        [VARIANT_TYPES.GROWTH]: {
          field: 'Cloud Revenue YOY%',
          headerName: 'Cloud Revenue',
          sublabel: 'YoY%',
          dbField: 'cloudRevenueyoy',
          format: METRIC_FORMATS.PERCENTAGE,
          proMetric: true,
          views: {
            compSheet: true,
            valuation: {
              axis: 'x',
            },
          },
          metadata: {
            displayGraph: true,
          },
        },
      },
    },

    transactionRevenue: {
      id: 'transactionRevenue',
      field: 'Transaction Revenue',
      group: METRIC_GROUPS.TOP_LINE,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'Transaction Revenue',
          dbField: 'transactionRevenue',
          format: METRIC_FORMATS.CURRENCY,
          proMetric: true,
          views: {
            compSheet: true,
            valuation: {
              axis: 'x',
            },
          },
          metadata: {
            fontWeight: 400,
            tab: 2,
            calc: ['% Growth', '$ Change', '% of Revenue'],
          },
        },
        [VARIANT_TYPES.GROWTH]: {
          field: 'Transaction Revenue YOY%',
          headerName: 'Transaction Revenue',
          sublabel: 'YoY%',
          dbField: 'transactionRevenueyoy',
          format: METRIC_FORMATS.PERCENTAGE,
          proMetric: true,
          views: {
            compSheet: true,
            valuation: {
              axis: 'x',
            },
          },
          metadata: {
            displayGraph: true,
          },
        },
      },
    },

    recurringRevenue: {
      id: 'recurringRevenue',
      field: 'Recurring Revenue',
      group: METRIC_GROUPS.TOP_LINE,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'Recurring Revenue',
          dbField: 'recurringRevenue',
          format: METRIC_FORMATS.CURRENCY,
          proMetric: true,
          views: {
            compSheet: true,
            valuation: {
              axis: 'x',
            },
          },
          metadata: {
            fontWeight: 400,
            tab: 2,
            calc: ['% Growth', '$ Change', '% of Revenue'],
          },
        },
        [VARIANT_TYPES.GROWTH]: {
          field: 'Recurring Revenue YOY%',
          headerName: 'Recurring Revenue',
          sublabel: 'YoY%',
          dbField: 'recurringRevenueyoy',
          format: METRIC_FORMATS.PERCENTAGE,
          proMetric: true,
          views: {
            compSheet: true,
            valuation: {
              axis: 'x',
            },
          },
          metadata: {
            displayGraph: true,
          },
        },
      },
    },

    rpo: {
      id: 'rpo',
      field: 'RPO',
      group: METRIC_GROUPS.TOP_LINE,
      metricType: METRIC_TYPES.INSTANT,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'RPO',
          dbField: 'rpo',
          format: METRIC_FORMATS.CURRENCY,
          proMetric: false,
          views: {
            securityAnalysis: true,
            trajectory: {
              label: 'RPO',
              color: 'rgb(200, 142, 63)',
            },
          },
          metadata: {
            fontWeight: 500,
            tab: 1,
            calc: ['% Growth', '$ Change'],
          },
        },
        [VARIANT_TYPES.GROWTH]: {
          field: 'RPO YOY%',
          headerName: 'RPO',
          sublabel: 'YoY%',
          dbField: 'rpoyoy',
          format: METRIC_FORMATS.PERCENTAGE,
          proMetric: false,
          views: {
            compSheet: true,
            dashboard: true,
            valuation: {
              axis: 'x',
            },
            trended: true,
          },
          metadata: {
            displayGraph: true,
          },
        },
        [VARIANT_TYPES.ACCELERATION]: {
          field: 'RPO Accel',
          headerName: 'RPO',
          sublabel: 'Accel (pp)',
          dbField: 'rpoAccel',
          group: METRIC_GROUPS.ACCELERATION,
          format: METRIC_FORMATS.PERCENTAGE,
          proMetric: true,
          views: {
            accel: true,
            compSheet: true,
            dashboard: true,
          },
        },
      },
    },

    crpo: {
      id: 'crpo',
      field: 'Current RPO',
      group: METRIC_GROUPS.TOP_LINE,
      metricType: METRIC_TYPES.INSTANT,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'Current RPO',
          dbField: 'currentRpo',
          format: METRIC_FORMATS.CURRENCY,
          proMetric: false,
          views: {
            securityAnalysis: true,
            trajectory: {
              label: 'cRPO',
              color: 'rgb(240, 182, 103)',
            },
          },
          metadata: {
            fontWeight: 500,
            tab: 1,
            calc: ['% Growth', '$ Change'],
          },
        },
        [VARIANT_TYPES.GROWTH]: {
          field: 'CRPO YOY%',
          headerName: 'cRPO',
          sublabel: 'YoY%',
          dbField: 'currentRpoyoy',
          format: METRIC_FORMATS.PERCENTAGE,
          proMetric: true,
          views: {
            compSheet: true,
            dashboard: true,
            valuation: {
              axis: 'x',
            },
            trended: true,
          },
          metadata: {
            displayGraph: true,
          },
        },
        [VARIANT_TYPES.ACCELERATION]: {
          field: 'CRPO Accel',
          headerName: 'cRPO',
          sublabel: 'Accel (pp)',
          dbField: 'currentRpoAccel',
          group: METRIC_GROUPS.ACCELERATION,
          format: METRIC_FORMATS.PERCENTAGE,
          proMetric: true,
          views: {
            accel: true,
            compSheet: true,
            dashboard: true,
          },
        },
      },
    },

    deferredRevenue: {
      id: 'deferredRevenue',
      field: 'Deferred Revenue',
      group: METRIC_GROUPS.TOP_LINE,
      metricType: METRIC_TYPES.INSTANT,
      variants: {
        [VARIANT_TYPES.GROWTH]: {
          field: 'Deferred Revenue YOY%',
          headerName: 'Deferred Revenue',
          sublabel: 'YoY%',
          dbField: 'deferredRevenueyoy',
          format: METRIC_FORMATS.PERCENTAGE,
          proMetric: false,
          views: {
            compSheet: true,
            dashboard: true,
            valuation: {
              axis: 'x',
            },
          },
          metadata: {
            displayGraph: true,
          },
        },
      },
    },


  // ========== BOOKINGS METRICS ==========

    billings: {
      id: 'billings',
      field: 'Billings',
      group: METRIC_GROUPS.TOP_LINE,
      metricType: METRIC_TYPES.FLOW,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'Calculated Billings',
          dbField: 'calculatedBillings',
          format: METRIC_FORMATS.CURRENCY,
          proMetric: true,
          views: {
            securityAnalysis: true,
            trajectory: {
              label: 'Billings',
              color: 'rgb(192, 107, 86)',
              ttm: {
                label: 'TTM Billings',
                color: 'rgb(232, 147, 126)',
                sourceField: 'Calculated Billings',
              },
            },
          },
          metadata: {
            fontWeight: 500,
            tab: 1,
            calc: ['% Growth', '$ Change'],
          },
        },
        [VARIANT_TYPES.GROWTH]: {
          field: 'Billings YOY%',
          headerName: 'Billings',
          sublabel: 'YoY%',
          dbField: 'currentCalculatedBillingsyoy',
          format: METRIC_FORMATS.PERCENTAGE,
          proMetric: true,
          views: {
            compSheet: true,
            dashboard: true,
            valuation: {
              axis: 'x',
            },
            trended: true,
          },
          metadata: {
            displayGraph: true,
          },
        },
        [VARIANT_TYPES.TTM_GROWTH]: {
          field: 'TTM Billings YOY%',
          headerName: 'Billings',
          sublabel: 'TTM YoY%',
          dbField: 'currentCalculatedBillingsttmYoy',
          format: METRIC_FORMATS.PERCENTAGE,
          proMetric: true,
          views: {
            compSheet: true,
            dashboard: true,
            valuation: {
              axis: 'x',
            },
          },
          metadata: {
            displayGraph: true,
          },
        },
        [VARIANT_TYPES.ACCELERATION]: {
          field: 'Billings Accel',
          headerName: 'Billings',
          sublabel: 'Accel (pp)',
          dbField: 'currentCalculatedBillingsAccel',
          group: METRIC_GROUPS.ACCELERATION,
          format: METRIC_FORMATS.PERCENTAGE,
          proMetric: true,
          views: {
            accel: true,
            compSheet: true,
            dashboard: true,
          },
        },
        [VARIANT_TYPES.TTM_ACCELERATION]: {
          field: 'TTM Billings Accel',
          headerName: 'Billings',
          sublabel: 'TTM Accel (pp)',
          dbField: 'ttmCurrentCalculatedBillingsAccel',
          group: METRIC_GROUPS.ACCELERATION,
          format: METRIC_FORMATS.PERCENTAGE,
          proMetric: true,
          views: {
            accel: true,
            compSheet: true,
          },
        },
      },
    },

    bookings: {
      id: 'bookings',
      field: 'Bookings',
      group: METRIC_GROUPS.TOP_LINE,
      metricType: METRIC_TYPES.FLOW,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'Bookings',
          dbField: 'calculatedBookings',
          format: METRIC_FORMATS.CURRENCY,
          proMetric: true,
          views: {
            securityAnalysis: true,
            trajectory: {
              label: 'Bookings',
              color: 'rgb(82, 133, 62)',
              ttm: {
                label: 'TTM Bookings',
                color: 'rgb(76, 175, 80)',
                sourceField: 'Bookings',
              },
            },
          },
          metadata: {
            fontWeight: 500,
            tab: 1,
            calc: ['% Growth', '$ Change'],
          },
        },
        [VARIANT_TYPES.GROWTH]: {
          field: 'Bookings YOY%',
          headerName: 'Bookings',
          sublabel: 'YoY%',
          dbField: 'calculatedBookingsyoy',
          format: METRIC_FORMATS.PERCENTAGE,
          proMetric: true,
          views: {
            compSheet: true,
            dashboard: true,
            valuation: {
              axis: 'x',
            },
            trended: true,
          },
          metadata: {
            displayGraph: true,
          },
        },
        [VARIANT_TYPES.TTM_GROWTH]: {
          field: 'TTM Bookings YOY%',
          headerName: 'Bookings',
          sublabel: 'TTM YoY%',
          dbField: 'calculatedBookingsttmYoy',
          format: METRIC_FORMATS.PERCENTAGE,
          proMetric: true,
          views: {
            compSheet: true,
            dashboard: true,
            valuation: {
              axis: 'x',
            },
          },
          metadata: {
            displayGraph: true,
          },
        },
        [VARIANT_TYPES.ACCELERATION]: {
          field: 'Bookings Accel',
          headerName: 'Bookings',
          sublabel: 'Accel (pp)',
          dbField: 'calculatedBookingsAccel',
          group: METRIC_GROUPS.ACCELERATION,
          format: METRIC_FORMATS.PERCENTAGE,
          proMetric: true,
          views: {
            accel: true,
            compSheet: true,
            dashboard: true,
          },
        },
        [VARIANT_TYPES.TTM_ACCELERATION]: {
          field: 'TTM Bookings Accel',
          headerName: 'Bookings',
          sublabel: 'TTM Accel (pp)',
          dbField: 'ttmCalculatedBookingsAccel',
          group: METRIC_GROUPS.ACCELERATION,
          format: METRIC_FORMATS.PERCENTAGE,
          proMetric: true,
          views: {
            accel: true,
            compSheet: true,
          },
        },
      },
    },

    currentBookings: {
      id: 'currentBookings',
      field: 'Current Bookings',
      group: METRIC_GROUPS.TOP_LINE,
      metricType: METRIC_TYPES.FLOW,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'Current Bookings',
          dbField: 'currentCalculatedBookings',
          format: METRIC_FORMATS.CURRENCY,
          proMetric: true,
          views: {
            securityAnalysis: true,
            trajectory: {
              label: 'Current Bookings',
              color: 'rgb(122, 173, 102)',
              ttm: {
                label: 'TTM Current Bookings',
                color: 'rgb(129, 199, 132)',
                sourceField: 'Current Bookings',
              },
            },
          },
          metadata: {
            fontWeight: 500,
            tab: 1,
            calc: ['% Growth', '$ Change'],
          },
        },
        [VARIANT_TYPES.GROWTH]: {
          field: 'Current Bookings YOY%',
          headerName: 'Current Bookings',
          sublabel: 'YoY%',
          dbField: 'currentCalculatedBookingsyoy',
          format: METRIC_FORMATS.PERCENTAGE,
          proMetric: true,
          views: {
            compSheet: true,
            dashboard: true,
            valuation: {
              axis: 'x',
            },
            trended: true,
          },
          metadata: {
            displayGraph: true,
          },
        },
        [VARIANT_TYPES.TTM_GROWTH]: {
          field: 'TTM Current Bookings YOY%',
          headerName: 'Current Bookings',
          sublabel: 'TTM YoY%',
          dbField: 'currentCalculatedBookingsttmYoy',
          format: METRIC_FORMATS.PERCENTAGE,
          proMetric: true,
          views: {
            compSheet: true,
            dashboard: true,
            valuation: {
              axis: 'x',
            },
          },
          metadata: {
            displayGraph: true,
          },
        },
        [VARIANT_TYPES.ACCELERATION]: {
          field: 'Current Bookings Accel',
          headerName: 'Current Bookings',
          sublabel: 'Accel (pp)',
          dbField: 'currentCalculatedBookingsAccel',
          group: METRIC_GROUPS.ACCELERATION,
          format: METRIC_FORMATS.PERCENTAGE,
          proMetric: true,
          views: {
            accel: true,
            compSheet: true,
            dashboard: true,
          },
        },
        [VARIANT_TYPES.TTM_ACCELERATION]: {
          field: 'TTM Current Bookings Accel',
          headerName: 'Current Bookings',
          sublabel: 'TTM Accel (pp)',
          dbField: 'ttmCurrentCalculatedBookingsAccel',
          group: METRIC_GROUPS.ACCELERATION,
          format: METRIC_FORMATS.PERCENTAGE,
          proMetric: true,
          views: {
            accel: true,
            compSheet: true,
          },
        },
      },
    },

    bestBookings: {
      id: 'bestBookings',
      field: 'Best Bookings',
      group: METRIC_GROUPS.TOP_LINE,
      metricType: METRIC_TYPES.FLOW,
      variants: {
        [VARIANT_TYPES.GROWTH]: {
          field: 'Best Bookings YOY%',
          headerName: 'Best Bookings',
          sublabel: 'YoY%',
          dbField: 'bestRevenueyoy',
          format: METRIC_FORMATS.PERCENTAGE,
          proMetric: true,
          views: {
            compSheet: true,
            dashboard: true,
            valuation: {
              axis: 'x',
            },
          },
          metadata: {
            displayGraph: true,
          },
        },
        [VARIANT_TYPES.TTM_GROWTH]: {
          field: 'TTM Best Bookings YOY%',
          headerName: 'Best Bookings',
          sublabel: 'TTM YoY%',
          dbField: 'bestRevenuettmYoy',
          format: METRIC_FORMATS.PERCENTAGE,
          proMetric: true,
          views: {
            compSheet: true,
            dashboard: true,
            valuation: {
              axis: 'x',
            },
          },
          metadata: {
            displayGraph: true,
          },
        },
      },
    },


  // ========== INCOME STATEMENT ==========

    cogs: {
      id: 'cogs',
      field: 'Cost of Goods Sold',
      group: METRIC_GROUPS.PROFITABILITY,
      metricType: METRIC_TYPES.FLOW,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'COGs',
          dbField: 'cogs',
          format: METRIC_FORMATS.CURRENCY,
          proMetric: false,
          views: {
            securityAnalysis: true,
          },
          metadata: {
            fontWeight: 400,
            tab: 2,
            calc: ['% Growth', '$ Change', '% of Revenue'],
          },
        },
      },
    },

    grossProfit: {
      id: 'grossProfit',
      field: 'Gross Profit',
      group: METRIC_GROUPS.PROFITABILITY,
      metricType: METRIC_TYPES.FLOW,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'Gross Profit',
          dbField: 'grossProfit',
          format: METRIC_FORMATS.CURRENCY,
          proMetric: false,
          views: {
            securityAnalysis: true,
          },
          metadata: {
            fontWeight: 500,
            tab: 1,
            calc: ['% Growth', '$ Change', '% of Revenue'],
          },
        },
      },
    },

    grossMargin: {
      id: 'grossMargin',
      field: 'Gross Margin',
      group: METRIC_GROUPS.PROFITABILITY,
      metricType: METRIC_TYPES.FLOW,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'Gross Margin',
          dbField: 'grossMargin',
          format: METRIC_FORMATS.CURRENCY,
          proMetric: false,
          views: {
            securityAnalysis: true,
          },
          metadata: {
            fontWeight: 500,
            tab: 1,
            calc: ['% Growth', '$ Change', '% of Revenue'],
          },
        },
        [VARIANT_TYPES.GROWTH]: {
          field: 'Gross Margin YOY%',
          headerName: 'Gross Margin',
          sublabel: 'YoY%',
          dbField: 'grossMarginyoy',
          format: METRIC_FORMATS.PERCENTAGE,
          proMetric: true,
          views: {
            compSheet: true,
            dashboard: true,
            valuation: {
              axis: 'x',
            },
            trended: true,
          },
          metadata: {
            displayGraph: true,
          },
        },
        [VARIANT_TYPES.TTM_GROWTH]: {
          field: 'TTM Gross Margin YOY%',
          headerName: 'Gross Margin',
          sublabel: 'TTM YoY%',
          dbField: 'grossMarginttmYoy',
          format: METRIC_FORMATS.PERCENTAGE,
          proMetric: true,
          views: {
            compSheet: true,
            dashboard: true,
            valuation: {
              axis: 'x',
            },
          },
          metadata: {
            displayGraph: true,
          },
        },
        [VARIANT_TYPES.PERCENT_OF]: {
          field: 'GM % of Sales',
          headerName: 'GM %',
          sublabel: 'TTM',
          dbField: 'gmPercentOfSales',
          format: METRIC_FORMATS.PERCENTAGE,
          proMetric: true,
          views: {
            compSheet: true,
            dashboard: true,
            trended: true,
            margins: {
              label: 'Gross Margin',
              color: 'rgb(102,187,106)',
              type: 'margin',
            },
          },
          metadata: {
            displayGraph: true,
          },
        },
      },
    },

    salesMarketing: {
      id: 'salesMarketing',
      field: 'Sales & Marketing',
      group: METRIC_GROUPS.PROFITABILITY,
      metricType: METRIC_TYPES.FLOW,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'Sales & Marketing',
          dbField: 'salesMarketing',
          format: METRIC_FORMATS.CURRENCY,
          proMetric: false,
          views: {
            securityAnalysis: true,
          },
          metadata: {
            fontWeight: 400,
            tab: 2,
            calc: ['% Growth', '$ Change', '% of Revenue'],
          },
        },
        [VARIANT_TYPES.PERCENT_OF]: {
          field: 'S&M % of Sales',
          headerName: 'S&M %',
          sublabel: 'TTM',
          dbField: 'smPercentOfSales',
          format: METRIC_FORMATS.PERCENTAGE,
          proMetric: true,
          views: {
            compSheet: true,
            dashboard: true,
            trended: true,
            margins: {
              label: 'S&M %',
              color: 'rgb(244,67,54)',
              type: 'opex',
            },
          },
          metadata: {
            displayGraph: true,
          },
        },
      },
    },

    researchDevelopment: {
      id: 'researchDevelopment',
      field: 'Research & Development',
      group: METRIC_GROUPS.PROFITABILITY,
      metricType: METRIC_TYPES.FLOW,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'Research & Development',
          dbField: 'researchDevelopment',
          format: METRIC_FORMATS.CURRENCY,
          proMetric: false,
          views: {
            securityAnalysis: true,
          },
          metadata: {
            fontWeight: 400,
            tab: 2,
            calc: ['% Growth', '$ Change', '% of Revenue'],
          },
        },
        [VARIANT_TYPES.PERCENT_OF]: {
          field: 'R&D % of Sales',
          headerName: 'R&D %',
          sublabel: 'TTM',
          dbField: 'rdPercentOfSales',
          format: METRIC_FORMATS.PERCENTAGE,
          proMetric: true,
          views: {
            compSheet: true,
            dashboard: true,
            trended: true,
            margins: {
              label: 'R&D %',
              color: 'rgb(33,150,243)',
              type: 'opex',
            },
          },
          metadata: {
            displayGraph: true,
          },
        },
      },
    },

    generalAdmin: {
      id: 'generalAdmin',
      field: 'General & Administrative',
      group: METRIC_GROUPS.PROFITABILITY,
      metricType: METRIC_TYPES.FLOW,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'General & Administrative',
          dbField: 'generalAdmin',
          format: METRIC_FORMATS.CURRENCY,
          proMetric: false,
          views: {
            securityAnalysis: true,
          },
          metadata: {
            fontWeight: 400,
            tab: 2,
            calc: ['% Growth', '$ Change', '% of Revenue'],
          },
        },
        [VARIANT_TYPES.PERCENT_OF]: {
          field: 'G&A % of Sales',
          headerName: 'G&A %',
          sublabel: 'TTM',
          dbField: 'gaPercentOfSales',
          format: METRIC_FORMATS.PERCENTAGE,
          proMetric: true,
          views: {
            compSheet: true,
            dashboard: true,
            trended: true,
            margins: {
              label: 'G&A %',
              color: 'rgb(156,39,176)',
              type: 'opex',
            },
          },
          metadata: {
            displayGraph: true,
          },
        },
      },
    },

    salesGeneralAndAdministrative: {
      id: 'salesGeneralAndAdministrative',
      field: 'Sales, General & Administrative',
      group: METRIC_GROUPS.PROFITABILITY,
      metricType: METRIC_TYPES.FLOW,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'Sales, General & Administrative',
          dbField: 'salesGeneralAndAdministrative',
          format: METRIC_FORMATS.CURRENCY,
          proMetric: false,
          views: {
            securityAnalysis: true,
          },
        },
      },
    },

    operatingIncome: {
      id: 'operatingIncome',
      field: 'Operating Income',
      group: METRIC_GROUPS.PROFITABILITY,
      metricType: METRIC_TYPES.FLOW,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'Operating Income',
          dbField: 'operatingIncome',
          format: METRIC_FORMATS.CURRENCY,
          proMetric: false,
          views: {
            securityAnalysis: true,
          },
          metadata: {
            fontWeight: 500,
            tab: 1,
            calc: ['% Growth', '$ Change', '% of Revenue'],
          },
        },
      },
    },

    interestExpense: {
      id: 'interestExpense',
      field: 'Interest Expense',
      group: METRIC_GROUPS.PROFITABILITY,
      metricType: METRIC_TYPES.FLOW,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'Interest Expense',
          dbField: 'interestExpense',
          format: METRIC_FORMATS.CURRENCY,
          proMetric: false,
          views: {
            securityAnalysis: true,
          },
          metadata: {
            fontWeight: 400,
            tab: 2,
          },
        },
      },
    },

    interestIncome: {
      id: 'interestIncome',
      field: 'Interest Income',
      group: METRIC_GROUPS.PROFITABILITY,
      metricType: METRIC_TYPES.FLOW,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'Interest Income',
          dbField: 'interestIncome',
          format: METRIC_FORMATS.CURRENCY,
          proMetric: false,
          views: {
            securityAnalysis: true,
          },
          metadata: {
            fontWeight: 400,
            tab: 2,
          },
        },
      },
    },

    incomeTaxExpense: {
      id: 'incomeTaxExpense',
      field: 'Income Tax Expense',
      group: METRIC_GROUPS.PROFITABILITY,
      metricType: METRIC_TYPES.FLOW,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'Income Tax Expense',
          dbField: 'incomeTaxExpense',
          format: METRIC_FORMATS.CURRENCY,
          proMetric: false,
          views: {
            securityAnalysis: true,
          },
          metadata: {
            fontWeight: 400,
            tab: 2,
          },
        },
      },
    },

    netIncome: {
      id: 'netIncome',
      field: 'Net Income',
      group: METRIC_GROUPS.PROFITABILITY,
      metricType: METRIC_TYPES.FLOW,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'Net Income',
          dbField: 'netIncome',
          format: METRIC_FORMATS.CURRENCY,
          proMetric: false,
          views: {
            securityAnalysis: true,
          },
          metadata: {
            fontWeight: 500,
            tab: 1,
            calc: ['% Growth', '$ Change', '% of Revenue'],
          },
        },
      },
    },

    stockBasedCompensation: {
      id: 'stockBasedCompensation',
      field: 'Stock-Based Compensation',
      group: METRIC_GROUPS.PROFITABILITY,
      metricType: METRIC_TYPES.FLOW,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'Stock-Based Compensation',
          dbField: 'stockBasedCompensation',
          format: METRIC_FORMATS.CURRENCY,
          proMetric: false,
          views: {
            securityAnalysis: true,
          },
          metadata: {
            fontWeight: 400,
            tab: 2,
            calc: ['% of Revenue'],
          },
        },
      },
    },

    sbc_cogs: {
      id: 'sbc_cogs',
      field: 'SBC — COGS',
      group: METRIC_GROUPS.PROFITABILITY,
      metricType: METRIC_TYPES.FLOW,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'SBC — COGS',
          dbField: 'sbc_cogs',
          format: METRIC_FORMATS.CURRENCY,
          proMetric: false,
          views: {
            securityAnalysis: true,
          },
          metadata: {
            fontWeight: 400,
            tab: 3,
          },
        },
      },
    },

    sbc_rnd: {
      id: 'sbc_rnd',
      field: 'SBC — R&D',
      group: METRIC_GROUPS.PROFITABILITY,
      metricType: METRIC_TYPES.FLOW,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'SBC — R&D',
          dbField: 'sbc_rnd',
          format: METRIC_FORMATS.CURRENCY,
          proMetric: false,
          views: {
            securityAnalysis: true,
          },
          metadata: {
            fontWeight: 400,
            tab: 3,
          },
        },
      },
    },

    sbc_sm: {
      id: 'sbc_sm',
      field: 'SBC — S&M',
      group: METRIC_GROUPS.PROFITABILITY,
      metricType: METRIC_TYPES.FLOW,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'SBC — S&M',
          dbField: 'sbc_sm',
          format: METRIC_FORMATS.CURRENCY,
          proMetric: false,
          views: {
            securityAnalysis: true,
          },
          metadata: {
            fontWeight: 400,
            tab: 3,
          },
        },
      },
    },

    sbc_gna: {
      id: 'sbc_gna',
      field: 'SBC — G&A',
      group: METRIC_GROUPS.PROFITABILITY,
      metricType: METRIC_TYPES.FLOW,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'SBC — G&A',
          dbField: 'sbc_gna',
          format: METRIC_FORMATS.CURRENCY,
          proMetric: false,
          views: {
            securityAnalysis: true,
          },
          metadata: {
            fontWeight: 400,
            tab: 3,
          },
        },
      },
    },


  // ========== CASH FLOW ==========

    cashFlowFromOperations: {
      id: 'cashFlowFromOperations',
      field: 'Operating Cash Flow',
      group: METRIC_GROUPS.PROFITABILITY,
      metricType: METRIC_TYPES.FLOW,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'Operating Cash Flow',
          dbField: 'cashFlowFromOperations',
          format: METRIC_FORMATS.CURRENCY,
          proMetric: false,
          views: {
            securityAnalysis: true,
          },
          metadata: {
            fontWeight: 400,
            tab: 1,
            calc: ['% of Revenue'],
          },
        },
      },
    },

    capex: {
      id: 'capex',
      field: 'Capital Expenditures',
      group: METRIC_GROUPS.PROFITABILITY,
      metricType: METRIC_TYPES.FLOW,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'Capital Expenditures',
          dbField: 'capex',
          format: METRIC_FORMATS.CURRENCY,
          proMetric: false,
          views: {
            securityAnalysis: true,
          },
          metadata: {
            fontWeight: 400,
            tab: 2,
          },
        },
      },
    },

    freeCashFlow: {
      id: 'freeCashFlow',
      field: 'Free Cash Flow',
      group: METRIC_GROUPS.PROFITABILITY,
      metricType: METRIC_TYPES.FLOW,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'Free Cash Flow',
          dbField: 'freeCashFlow',
          format: METRIC_FORMATS.CURRENCY,
          proMetric: false,
          views: {
            securityAnalysis: true,
          },
          metadata: {
            fontWeight: 500,
            tab: 1,
            calc: ['% Growth', '$ Change', '% of Revenue'],
          },
        },
        [VARIANT_TYPES.PERCENT_OF]: {
          field: 'FCF % of Sales',
          headerName: 'FCF %',
          sublabel: 'TTM',
          dbField: 'fcfPercentOfSales',
          format: METRIC_FORMATS.PERCENTAGE,
          proMetric: false,
          views: {
            compSheet: true,
            dashboard: true,
            trended: true,
            margins: {
              label: 'FCF Margin',
              color: 'rgb(145,166,205)',
              type: 'margin',
            },
          },
          metadata: {
            displayGraph: true,
          },
        },
      },
    },

    capitalizedSoftware: {
      id: 'capitalizedSoftware',
      field: 'Capitalized Software',
      group: 'CASH_FLOW',
      metricType: METRIC_TYPES.FLOW,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'Capitalized Software',
          dbField: 'capitalizedSoftware',
          format: METRIC_FORMATS.CURRENCY,
          proMetric: false,
          views: {
            securityAnalysis: true,
          },
        },
      },
    },


  // ========== BALANCE SHEET ==========

    cashAndCashEquivalents: {
      id: 'cashAndCashEquivalents',
      field: 'Cash & Equivalents',
      group: METRIC_GROUPS.COMPANY_STATS,
      metricType: METRIC_TYPES.INSTANT,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'Cash & Equivalents',
          dbField: 'cashAndCashEquivalents',
          format: METRIC_FORMATS.CURRENCY,
          proMetric: false,
          views: {
            securityAnalysis: true,
          },
          metadata: {
            fontWeight: 400,
            tab: 1,
          },
        },
      },
    },

    marketableSecuritiesCurrent: {
      id: 'marketableSecuritiesCurrent',
      field: 'Marketable Securities (Current)',
      group: METRIC_GROUPS.COMPANY_STATS,
      metricType: METRIC_TYPES.INSTANT,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'Marketable Securities (Current)',
          dbField: 'marketableSecuritiesCurrent',
          format: METRIC_FORMATS.CURRENCY,
          proMetric: false,
          views: {
            securityAnalysis: true,
          },
        },
      },
    },

    debtCurrent: {
      id: 'debtCurrent',
      field: 'Debt (Current)',
      group: METRIC_GROUPS.COMPANY_STATS,
      metricType: METRIC_TYPES.INSTANT,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'Debt (Current)',
          dbField: 'debtCurrent',
          format: METRIC_FORMATS.CURRENCY,
          proMetric: false,
          views: {
            securityAnalysis: true,
          },
        },
      },
    },

    debtNonCurrent: {
      id: 'debtNonCurrent',
      field: 'Long-Term Debt',
      group: METRIC_GROUPS.COMPANY_STATS,
      metricType: METRIC_TYPES.INSTANT,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'Long-Term Debt',
          dbField: 'debtNonCurrent',
          format: METRIC_FORMATS.CURRENCY,
          proMetric: false,
          views: {
            securityAnalysis: true,
          },
          metadata: {
            fontWeight: 400,
            tab: 2,
          },
        },
      },
    },

    goodwill: {
      id: 'goodwill',
      field: 'Goodwill',
      group: METRIC_GROUPS.COMPANY_STATS,
      metricType: METRIC_TYPES.INSTANT,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'Goodwill',
          dbField: 'goodwill',
          format: METRIC_FORMATS.CURRENCY,
          proMetric: false,
          views: {
            securityAnalysis: true,
          },
          metadata: {
            fontWeight: 400,
            tab: 2,
          },
        },
      },
    },

    accountsReceivableNet: {
      id: 'accountsReceivableNet',
      field: 'Accounts Receivable',
      group: METRIC_GROUPS.COMPANY_STATS,
      metricType: METRIC_TYPES.INSTANT,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'Accounts Receivable',
          dbField: 'accountsReceivableNet',
          format: METRIC_FORMATS.CURRENCY,
          proMetric: false,
          views: {
            securityAnalysis: true,
          },
          metadata: {
            fontWeight: 400,
            tab: 2,
          },
        },
      },
    },

    deferredRevenueCurrent: {
      id: 'deferredRevenueCurrent',
      field: 'Deferred Revenue (Current)',
      group: METRIC_GROUPS.TOP_LINE,
      metricType: METRIC_TYPES.INSTANT,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'Deferred Revenue (Current)',
          dbField: 'deferredRevenueCurrent',
          format: METRIC_FORMATS.CURRENCY,
          proMetric: false,
          views: {
            securityAnalysis: true,
          },
          metadata: {
            fontWeight: 400,
            tab: 2,
          },
        },
        [VARIANT_TYPES.ACCELERATION]: {
          field: 'Deferred Revenue Accel',
          headerName: 'Deferred Revenue',
          sublabel: 'Accel (pp)',
          dbField: 'deferredRevenueCurrentAccel',
          group: METRIC_GROUPS.ACCELERATION,
          format: METRIC_FORMATS.PERCENTAGE,
          proMetric: true,
          views: {
            accel: true,
            compSheet: true,
          },
        },
      },
    },

    assetsTotal: {
      id: 'assetsTotal',
      field: 'Total Assets',
      group: METRIC_GROUPS.COMPANY_STATS,
      metricType: METRIC_TYPES.INSTANT,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'Total Assets',
          dbField: 'assetsTotal',
          format: METRIC_FORMATS.CURRENCY,
          proMetric: false,
          views: {
            securityAnalysis: true,
          },
          metadata: {
            fontWeight: 500,
            tab: 1,
          },
        },
      },
    },

    liabilitiesTotal: {
      id: 'liabilitiesTotal',
      field: 'Total Liabilities',
      group: METRIC_GROUPS.COMPANY_STATS,
      metricType: METRIC_TYPES.INSTANT,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'Total Liabilities',
          dbField: 'liabilitiesTotal',
          format: METRIC_FORMATS.CURRENCY,
          proMetric: false,
          views: {
            securityAnalysis: true,
          },
          metadata: {
            fontWeight: 500,
            tab: 1,
          },
        },
      },
    },

    equityTotal: {
      id: 'equityTotal',
      field: 'Total Stockholders\' Equity',
      group: METRIC_GROUPS.COMPANY_STATS,
      metricType: METRIC_TYPES.INSTANT,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'Total Stockholders\' Equity',
          dbField: 'equityTotal',
          format: METRIC_FORMATS.CURRENCY,
          proMetric: false,
          views: {
            securityAnalysis: true,
          },
          metadata: {
            fontWeight: 500,
            tab: 1,
          },
        },
      },
    },


  // ========== UNIT ECONOMICS ==========

    customers: {
      id: 'customers',
      field: 'Customers',
      group: METRIC_GROUPS.UNIT_ECONOMICS,
      metricType: METRIC_TYPES.INSTANT,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'Customers',
          dbField: 'customers',
          format: METRIC_FORMATS.NUMBER,
          proMetric: false,
          colDefOverrides: {
  width: 95,
  minWidth: 90,
},
          views: {
            valuation: {
              axis: 'x',
            },
            securityAnalysis: true,
            trajectory: {
              label: 'Customers',
              color: 'rgb(95, 116, 155)',
            },
          },
          metadata: {
            fontWeight: 400,
            tab: 1,
            calc: ['% Growth', '$ Change'],
          },
        },
        [VARIANT_TYPES.GROWTH]: {
          field: 'Customers YOY%',
          headerName: 'Customers',
          sublabel: 'YoY%',
          dbField: 'customersyoy',
          format: METRIC_FORMATS.PERCENTAGE,
          proMetric: true,
          colDefOverrides: {
  width: 95,
  minWidth: 90,
},
          views: {
            compSheet: true,
            dashboard: true,
            valuation: {
              axis: 'x',
            },
            trended: true,
          },
          metadata: {
            displayGraph: true,
          },
        },
        [VARIANT_TYPES.ACCELERATION]: {
          field: 'Customers Accel',
          headerName: 'Customers',
          sublabel: 'Accel (pp)',
          dbField: 'customersAccel',
          group: METRIC_GROUPS.ACCELERATION,
          format: METRIC_FORMATS.PERCENTAGE,
          proMetric: true,
          views: {
            accel: true,
            compSheet: true,
            dashboard: true,
          },
        },
      },
    },

    customers10User: {
      id: 'customers10User',
      field: 'Paid Customers >10 Users',
      group: METRIC_GROUPS.UNIT_ECONOMICS,
      metricType: METRIC_TYPES.INSTANT,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'Paid Customers >10 Users',
          dbField: 'customers10User',
          format: METRIC_FORMATS.NUMBER,
          proMetric: false,
          views: {
            securityAnalysis: true,
          },
          metadata: {
            fontWeight: 400,
            tab: 1,
          },
        },
      },
    },

    customersGt50: {
      id: 'customersGt50',
      field: 'Customers >$50',
      group: METRIC_GROUPS.UNIT_ECONOMICS,
      metricType: METRIC_TYPES.INSTANT,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'Customers >$50',
          dbField: 'customersGt50',
          format: METRIC_FORMATS.NUMBER,
          proMetric: false,
          views: {
            securityAnalysis: true,
          },
        },
      },
    },

    customersGt5k: {
      id: 'customersGt5k',
      field: 'Customers >$5k',
      group: METRIC_GROUPS.UNIT_ECONOMICS,
      metricType: METRIC_TYPES.INSTANT,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'Customers >$5k',
          dbField: 'customersGt5k',
          format: METRIC_FORMATS.NUMBER,
          proMetric: false,
          views: {
            securityAnalysis: true,
          },
        },
      },
    },

    customersGt10k: {
      id: 'customersGt10k',
      field: 'Customers >$10k',
      group: METRIC_GROUPS.UNIT_ECONOMICS,
      metricType: METRIC_TYPES.INSTANT,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'Customers >$10k',
          dbField: 'customersGt10k',
          format: METRIC_FORMATS.NUMBER,
          proMetric: false,
          views: {
            securityAnalysis: true,
          },
        },
      },
    },

    customersGt50k: {
      id: 'customersGt50k',
      field: 'Customers >$50k',
      group: METRIC_GROUPS.UNIT_ECONOMICS,
      metricType: METRIC_TYPES.INSTANT,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'Customers >$50k',
          dbField: 'customersGt50k',
          format: METRIC_FORMATS.NUMBER,
          proMetric: false,
          views: {
            securityAnalysis: true,
          },
          metadata: {
            fontWeight: 400,
            tab: 1,
          },
        },
      },
    },

    customers100k: {
      id: 'customers100k',
      field: 'Customers >$100k',
      group: METRIC_GROUPS.UNIT_ECONOMICS,
      metricType: METRIC_TYPES.INSTANT,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'Customers >$100k',
          dbField: 'customers100k',
          sourceMetricKey: 'customersGt100k',
          format: METRIC_FORMATS.NUMBER,
          proMetric: false,
          colDefOverrides: {
  width: 95,
  minWidth: 90,
},
          views: {
            valuation: {
              axis: 'x',
            },
            securityAnalysis: true,
            trajectory: {
              label: 'Customers >$100k',
              color: 'rgb(135, 156, 195)',
            },
          },
          metadata: {
            fontWeight: 400,
            tab: 1,
            calc: ['% Growth', '$ Change'],
          },
        },
        [VARIANT_TYPES.GROWTH]: {
          field: 'Customers >$100k YOY%',
          headerName: 'Customers >$100k',
          sublabel: 'YoY%',
          dbField: 'customers100kyoy',
          format: METRIC_FORMATS.PERCENTAGE,
          proMetric: false,
          colDefOverrides: {
  width: 95,
  minWidth: 90,
},
          views: {
            compSheet: true,
            dashboard: true,
            valuation: {
              axis: 'x',
            },
          },
          metadata: {
            displayGraph: true,
          },
        },
        [VARIANT_TYPES.ACCELERATION]: {
          field: 'Customers >$100k Accel',
          headerName: 'Customers >$100k',
          sublabel: 'Accel (pp)',
          dbField: 'customersGt100kAccel',
          group: METRIC_GROUPS.ACCELERATION,
          format: METRIC_FORMATS.PERCENTAGE,
          proMetric: true,
          views: {
            accel: true,
            compSheet: true,
            dashboard: true,
          },
        },
      },
    },

    customersGt250k: {
      id: 'customersGt250k',
      field: 'Customers >$250k',
      group: METRIC_GROUPS.UNIT_ECONOMICS,
      metricType: METRIC_TYPES.INSTANT,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'Customers >$250k',
          dbField: 'customersGt250k',
          format: METRIC_FORMATS.NUMBER,
          proMetric: false,
          views: {
            securityAnalysis: true,
          },
        },
      },
    },

    customersGt300k: {
      id: 'customersGt300k',
      field: 'Customers >$300k',
      group: METRIC_GROUPS.UNIT_ECONOMICS,
      metricType: METRIC_TYPES.INSTANT,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'Customers >$300k',
          dbField: 'customersGt300k',
          format: METRIC_FORMATS.NUMBER,
          proMetric: false,
          views: {
            securityAnalysis: true,
          },
        },
      },
    },

    customersGt500k: {
      id: 'customersGt500k',
      field: 'Customers >$500k',
      group: METRIC_GROUPS.UNIT_ECONOMICS,
      metricType: METRIC_TYPES.INSTANT,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'Customers >$500k',
          dbField: 'customersGt500k',
          format: METRIC_FORMATS.NUMBER,
          proMetric: false,
          views: {
            securityAnalysis: true,
          },
          metadata: {
            fontWeight: 400,
            tab: 1,
          },
        },
      },
    },

    customersGt1M: {
      id: 'customersGt1M',
      field: 'Customers >$1M',
      group: METRIC_GROUPS.UNIT_ECONOMICS,
      metricType: METRIC_TYPES.INSTANT,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'Customers >$1M',
          dbField: 'customersGt1M',
          format: METRIC_FORMATS.NUMBER,
          proMetric: false,
          views: {
            securityAnalysis: true,
          },
        },
      },
    },

    customersGt5M: {
      id: 'customersGt5M',
      field: 'Customers >$5M',
      group: METRIC_GROUPS.UNIT_ECONOMICS,
      metricType: METRIC_TYPES.INSTANT,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'Customers >$5M',
          dbField: 'customersGt5M',
          format: METRIC_FORMATS.NUMBER,
          proMetric: false,
          views: {
            securityAnalysis: true,
          },
        },
      },
    },

    payingUsers: {
      id: 'payingUsers',
      field: 'Paying Users',
      group: METRIC_GROUPS.UNIT_ECONOMICS,
      metricType: METRIC_TYPES.INSTANT,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'Paying Users',
          dbField: 'payingUsers',
          format: METRIC_FORMATS.NUMBER,
          proMetric: false,
          views: {
            securityAnalysis: true,
          },
        },
      },
    },

    locations: {
      id: 'locations',
      field: 'Locations',
      group: METRIC_GROUPS.UNIT_ECONOMICS,
      metricType: METRIC_TYPES.INSTANT,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'Locations',
          dbField: 'locations',
          format: METRIC_FORMATS.NUMBER,
          proMetric: false,
          views: {
            securityAnalysis: true,
          },
        },
      },
    },

    paybackPeriod: {
      id: 'paybackPeriod',
      field: 'Payback Period',
      group: METRIC_GROUPS.UNIT_ECONOMICS,
      metricType: METRIC_TYPES.INSTANT,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'Payback Period',
          dbField: 'paybackPeriod',
          format: METRIC_FORMATS.NUMBER,
          proMetric: true,
          views: {
            compSheet: true,
            dashboard: true,
            valuation: {
              axis: 'x',
            },
            securityAnalysis: true,
            trended: true,
          },
          metadata: {
            fontWeight: 400,
            tab: 1,
            calc: ['$ Change'],
            sublabel: 'Months',
          },
        },
      },
    },

    revenuePerCustomer: {
      id: 'revenuePerCustomer',
      field: 'Revenue Per Customer',
      group: METRIC_GROUPS.BUSINESS_COMPOSITION,
      metricType: METRIC_TYPES.INSTANT,
      variants: {
        [VARIANT_TYPES.RATIO]: {
          field: 'Revenue/Customer',
          headerName: 'Revenue per Customer',
          dbField: 'revenuePerCustomer',
          format: METRIC_FORMATS.MONETARY_KPI,
          proMetric: true,
          views: {
            compSheet: true,
            dashboard: true,
            securityAnalysis: true,
            trended: true,
          },
          metadata: {
            fontWeight: 400,
            tab: 1,
            calc: ['% Growth', '$ Change'],
            displayGraph: true,
          },
        },
      },
    },

    nrr: {
      id: 'nrr',
      field: 'Net Retention Rate',
      group: METRIC_GROUPS.UNIT_ECONOMICS,
      metricType: METRIC_TYPES.INSTANT,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'NRR',
          headerName: 'NRR',
          sublabel: 'TTM%',
          dbField: 'nrr',
          format: METRIC_FORMATS.PERCENTAGE,
          proMetric: false,
          views: {
            compSheet: true,
            dashboard: true,
            valuation: {
              axis: 'x',
            },
            securityAnalysis: true,
            trended: true,
          },
          metadata: {
            fontWeight: 400,
            tab: 1,
            calc: ['$ Change'],
            displayGraph: true,
          },
        },
        [VARIANT_TYPES.ACCELERATION]: {
          field: 'NRR Accel',
          headerName: 'NRR',
          sublabel: 'Seq Chg (pp)',
          dbField: 'nrrAccel',
          group: METRIC_GROUPS.ACCELERATION,
          format: METRIC_FORMATS.PERCENTAGE,
          proMetric: true,
          views: {
            accel: true,
            compSheet: true,
            dashboard: true,
          },
        },
      },
    },

    cloudNrr: {
      id: 'cloudNrr',
      field: 'Cloud Net Retention Rate',
      group: METRIC_GROUPS.UNIT_ECONOMICS,
      metricType: METRIC_TYPES.INSTANT,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'Cloud NRR',
          headerName: 'Cloud NRR',
          sublabel: 'TTM%',
          dbField: 'cloudNrr',
          format: METRIC_FORMATS.PERCENTAGE,
          proMetric: false,
          views: {
            securityAnalysis: true,
          },
        },
      },
    },

    grr: {
      id: 'grr',
      field: 'Gross Retention Rate',
      group: METRIC_GROUPS.UNIT_ECONOMICS,
      metricType: METRIC_TYPES.INSTANT,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'GRR',
          headerName: 'GRR',
          sublabel: 'TTM%',
          dbField: 'grr',
          format: METRIC_FORMATS.PERCENTAGE,
          proMetric: false,
          views: {
            securityAnalysis: true,
          },
        },
      },
    },

    nrr10User: {
      id: 'nrr10User',
      field: 'NRR — Customers >10 Users',
      group: METRIC_GROUPS.UNIT_ECONOMICS,
      metricType: METRIC_TYPES.INSTANT,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'NRR — Customers >10 Users',
          headerName: 'NRR >10 Users',
          sublabel: 'TTM%',
          dbField: 'nrr10User',
          format: METRIC_FORMATS.PERCENTAGE,
          proMetric: false,
          views: {
            securityAnalysis: true,
          },
          metadata: {
            fontWeight: 400,
            tab: 1,
          },
        },
      },
    },

    nrr50k: {
      id: 'nrr50k',
      field: 'NRR — Customers >$50k ARR',
      group: METRIC_GROUPS.UNIT_ECONOMICS,
      metricType: METRIC_TYPES.INSTANT,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'NRR — Customers >$50k ARR',
          headerName: 'NRR >$50k ARR',
          sublabel: 'TTM%',
          dbField: 'nrr50k',
          format: METRIC_FORMATS.PERCENTAGE,
          proMetric: false,
          views: {
            securityAnalysis: true,
          },
          metadata: {
            fontWeight: 400,
            tab: 1,
          },
        },
      },
    },

    nrr100k: {
      id: 'nrr100k',
      field: 'NRR — Customers >$100k ARR',
      group: METRIC_GROUPS.UNIT_ECONOMICS,
      metricType: METRIC_TYPES.INSTANT,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'NRR — Customers >$100k ARR',
          headerName: 'NRR >$100k ARR',
          sublabel: 'TTM%',
          dbField: 'nrr100k',
          format: METRIC_FORMATS.PERCENTAGE,
          proMetric: false,
          views: {
            securityAnalysis: true,
          },
          metadata: {
            fontWeight: 400,
            tab: 1,
          },
        },
      },
    },


  // ========== EFFICIENCY METRICS ==========

    ruleOf40: {
      id: 'ruleOf40',
      field: 'Rule of 40',
      group: METRIC_GROUPS.UNIT_ECONOMICS,
      metricType: METRIC_TYPES.INSTANT,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'Rule of 40',
          headerName: 'Rule of 40',
          sublabel: 'TTM',
          dbField: 'ruleOf40values',
          format: METRIC_FORMATS.PERCENTAGE,
          proMetric: false,
          views: {
            compSheet: true,
            dashboard: true,
            valuation: {
              axis: 'x',
            },
            securityAnalysis: true,
            trended: true,
            margins: {
              label: 'Rule of 40',
              color: 'rgb(255,167,38)',
              type: 'margin',
            },
          },
          metadata: {
            fontWeight: 400,
            tab: 1,
            calc: ['$ Change'],
            displayGraph: true,
          },
        },
      },
    },

    ruleOfX: {
      id: 'ruleOfX',
      field: 'Rule of X',
      group: METRIC_GROUPS.UNIT_ECONOMICS,
      metricType: METRIC_TYPES.INSTANT,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'Rule of X',
          headerName: 'Rule of X',
          sublabel: 'TTM',
          dbField: 'ruleOfXvalues',
          format: METRIC_FORMATS.PERCENTAGE,
          proMetric: true,
          views: {
            compSheet: true,
            dashboard: true,
            valuation: {
              axis: 'x',
            },
            securityAnalysis: true,
            trended: true,
          },
          metadata: {
            fontWeight: 400,
            tab: 1,
            calc: ['$ Change'],
            displayGraph: true,
          },
        },
      },
    },

    ruleOfXBookings: {
      id: 'ruleOfXBookings',
      field: 'Rule of X Bookings',
      group: METRIC_GROUPS.UNIT_ECONOMICS,
      metricType: METRIC_TYPES.INSTANT,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'Rule of X Bookings',
          headerName: 'Rule of X Bookings',
          sublabel: 'TTM',
          dbField: 'ruleOfXBookingsvalues',
          format: METRIC_FORMATS.PERCENTAGE,
          proMetric: true,
          views: {
            compSheet: true,
            dashboard: true,
            valuation: {
              axis: 'x',
            },
            securityAnalysis: true,
            trended: true,
          },
          metadata: {
            fontWeight: 400,
            tab: 1,
            calc: ['$ Change'],
            displayGraph: true,
          },
        },
      },
    },


  // ========== BUSINESS COMPOSITION ==========

    cloudMix: {
      id: 'cloudMix',
      field: 'Cloud Mix',
      group: METRIC_GROUPS.BUSINESS_COMPOSITION,
      metricType: METRIC_TYPES.INSTANT,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'Cloud Mix %',
          headerName: 'Cloud Mix',
          sublabel: '%',
          dbField: 'cloudMix',
          format: METRIC_FORMATS.PERCENTAGE,
          proMetric: true,
          views: {
            compSheet: true,
            dashboard: true,
            securityAnalysis: true,
          },
          metadata: {
            fontWeight: 400,
            tab: 1,
            calc: ['$ Change'],
            displayGraph: true,
          },
        },
      },
    },

    gmv: {
      id: 'gmv',
      field: 'Gross Merchandise Volume',
      group: METRIC_GROUPS.BUSINESS_COMPOSITION,
      metricType: METRIC_TYPES.FLOW,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'GMV',
          dbField: 'gmv',
          format: METRIC_FORMATS.CURRENCY,
          proMetric: true,
          views: {
            securityAnalysis: true,
          },
        },
      },
    },

    gpv: {
      id: 'gpv',
      field: 'Gross Payment Volume',
      group: METRIC_GROUPS.BUSINESS_COMPOSITION,
      metricType: METRIC_TYPES.FLOW,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'GPV',
          dbField: 'gpv',
          format: METRIC_FORMATS.CURRENCY,
          proMetric: true,
          views: {
            securityAnalysis: true,
          },
        },
      },
    },

    gtv: {
      id: 'gtv',
      field: 'Gross Transaction Value',
      group: METRIC_GROUPS.BUSINESS_COMPOSITION,
      metricType: METRIC_TYPES.FLOW,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'GTV',
          dbField: 'gtv',
          format: METRIC_FORMATS.CURRENCY,
          proMetric: true,
          views: {
            securityAnalysis: true,
          },
        },
      },
    },

    merchantSolutions: {
      id: 'merchantSolutions',
      field: 'Merchant Solutions',
      group: METRIC_GROUPS.BUSINESS_COMPOSITION,
      metricType: METRIC_TYPES.FLOW,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'Merchant Solutions',
          dbField: 'merchantSolutions',
          format: METRIC_FORMATS.CURRENCY,
          proMetric: true,
          views: {
            securityAnalysis: true,
          },
        },
      },
    },

    subscriptionSolutions: {
      id: 'subscriptionSolutions',
      field: 'Subscription Solutions',
      group: METRIC_GROUPS.BUSINESS_COMPOSITION,
      metricType: METRIC_TYPES.FLOW,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'Subscription Solutions',
          dbField: 'subscriptionSolutions',
          format: METRIC_FORMATS.CURRENCY,
          proMetric: true,
          views: {
            securityAnalysis: true,
          },
        },
      },
    },

    transaction: {
      id: 'transaction',
      field: 'Transaction',
      group: METRIC_GROUPS.BUSINESS_COMPOSITION,
      metricType: METRIC_TYPES.FLOW,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'Transaction',
          dbField: 'transaction',
          format: METRIC_FORMATS.CURRENCY,
          proMetric: true,
          views: {
            securityAnalysis: true,
          },
        },
      },
    },

    perArr50k: {
      id: 'perArr50k',
      field: 'ARR Mix — Customers >$50k ARR',
      group: METRIC_GROUPS.BUSINESS_COMPOSITION,
      metricType: METRIC_TYPES.INSTANT,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'ARR Mix — Customers >$50k ARR',
          headerName: 'ARR Mix >$50k',
          sublabel: '%',
          dbField: 'perArr50k',
          format: METRIC_FORMATS.PERCENTAGE,
          proMetric: true,
          views: {
            securityAnalysis: true,
          },
          metadata: {
            fontWeight: 400,
            tab: 1,
          },
        },
      },
    },

    perArr100k: {
      id: 'perArr100k',
      field: 'ARR Mix — Customers >$100k ARR',
      group: METRIC_GROUPS.BUSINESS_COMPOSITION,
      metricType: METRIC_TYPES.INSTANT,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'ARR Mix — Customers >$100k ARR',
          headerName: 'ARR Mix >$100k',
          sublabel: '%',
          dbField: 'perArr100k',
          format: METRIC_FORMATS.PERCENTAGE,
          proMetric: true,
          views: {
            securityAnalysis: true,
          },
          metadata: {
            fontWeight: 400,
            tab: 1,
          },
        },
      },
    },

    perArr500k: {
      id: 'perArr500k',
      field: 'ARR Mix — Customers >$500k ARR',
      group: METRIC_GROUPS.BUSINESS_COMPOSITION,
      metricType: METRIC_TYPES.INSTANT,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'ARR Mix — Customers >$500k ARR',
          headerName: 'ARR Mix >$500k',
          sublabel: '%',
          dbField: 'perArr500k',
          format: METRIC_FORMATS.PERCENTAGE,
          proMetric: true,
          views: {
            securityAnalysis: true,
          },
          metadata: {
            fontWeight: 400,
            tab: 1,
          },
        },
      },
    },

    perArrNewProducts: {
      id: 'perArrNewProducts',
      field: 'ARR Mix — New Products',
      group: METRIC_GROUPS.BUSINESS_COMPOSITION,
      metricType: METRIC_TYPES.INSTANT,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'ARR Mix — New Products',
          headerName: 'New Products ARR Mix',
          sublabel: '%',
          dbField: 'perArrNewProducts',
          format: METRIC_FORMATS.PERCENTAGE,
          proMetric: true,
          views: {
            securityAnalysis: true,
          },
          metadata: {
            fontWeight: 400,
            tab: 1,
          },
        },
      },
    },

    serviceMix: {
      id: 'serviceMix',
      field: 'Service Mix',
      group: METRIC_GROUPS.BUSINESS_COMPOSITION,
      metricType: METRIC_TYPES.INSTANT,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'Service Mix %',
          headerName: 'Service Mix',
          sublabel: '%',
          dbField: 'serviceMix',
          format: METRIC_FORMATS.PERCENTAGE,
          proMetric: true,
          views: {
            compSheet: true,
            dashboard: true,
            securityAnalysis: true,
          },
          metadata: {
            fontWeight: 400,
            tab: 1,
            calc: ['$ Change'],
            displayGraph: true,
          },
        },
      },
    },

    transactionMix: {
      id: 'transactionMix',
      field: 'Transaction Mix',
      group: METRIC_GROUPS.BUSINESS_COMPOSITION,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'Transaction Mix %',
          headerName: 'Transaction Mix',
          sublabel: '%',
          dbField: 'transactionMix',
          format: METRIC_FORMATS.PERCENTAGE,
          proMetric: true,
          views: {
            compSheet: true,
            dashboard: true,
            securityAnalysis: true,
          },
          metadata: {
            fontWeight: 400,
            tab: 1,
            calc: ['$ Change'],
            displayGraph: true,
          },
        },
      },
    },

    recurringMix: {
      id: 'recurringMix',
      field: 'Recurring Mix',
      group: METRIC_GROUPS.BUSINESS_COMPOSITION,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'Recurring Mix %',
          headerName: 'Recurring Mix',
          sublabel: '%',
          dbField: 'recurringMix',
          format: METRIC_FORMATS.PERCENTAGE,
          proMetric: true,
          views: {
            compSheet: true,
            dashboard: true,
            securityAnalysis: true,
          },
          metadata: {
            fontWeight: 400,
            tab: 1,
            calc: ['$ Change'],
            displayGraph: true,
          },
        },
      },
    },

    currentRpoMix: {
      id: 'currentRpoMix',
      field: 'Current RPO Mix',
      group: METRIC_GROUPS.BUSINESS_COMPOSITION,
      metricType: METRIC_TYPES.INSTANT,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'Current RPO Mix %',
          headerName: 'cRPO Mix',
          sublabel: '%',
          dbField: 'currentRpoMix',
          format: METRIC_FORMATS.PERCENTAGE,
          proMetric: true,
          views: {
            compSheet: true,
            dashboard: true,
            securityAnalysis: true,
          },
          metadata: {
            fontWeight: 400,
            tab: 1,
            calc: ['$ Change'],
            displayGraph: true,
          },
        },
      },
    },

    rpoDuration: {
      id: 'rpoDuration',
      field: 'RPO Duration',
      group: METRIC_GROUPS.BUSINESS_COMPOSITION,
      metricType: METRIC_TYPES.INSTANT,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'RPO Duration',
          headerName: 'RPO Duration',
          sublabel: 'Years',
          dbField: 'rpoDuration',
          format: METRIC_FORMATS.NUMBER,
          proMetric: true,
          views: {
            dashboard: true,
            securityAnalysis: true,
          },
          metadata: {
            fontWeight: 400,
            tab: 1,
            calc: ['$ Change'],
            displayGraph: true,
          },
        },
      },
    },

    q4BookingsRatio: {
      id: 'q4BookingsRatio',
      field: 'Q4 Bookings Ratio',
      group: METRIC_GROUPS.BUSINESS_COMPOSITION,
      metricType: METRIC_TYPES.INSTANT,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'Q4 Bookings Ratio',
          headerName: 'Q4 Bookings Ratio',
          sublabel: '%',
          dbField: 'q4BookingsRatio',
          format: METRIC_FORMATS.PERCENTAGE,
          proMetric: true,
          views: {
            compSheet: true,
            dashboard: true,
            securityAnalysis: true,
          },
          metadata: {
            fontWeight: 400,
            tab: 1,
            calc: ['$ Change'],
            displayGraph: true,
          },
        },
      },
    },


  // ========== VALUATION METRICS ==========

    evSalesNtm: {
      id: 'evSalesNtm',
      field: 'EV / Sales NTM',
      group: METRIC_GROUPS.COMPANY_STATS,
      metricType: METRIC_TYPES.INSTANT,
      variants: {
        [VARIANT_TYPES.MULTIPLE]: {
          field: 'EV/Sales NTM',
          headerName: 'EV/Sales',
          sublabel: 'NTM',
          dbField: 'evToSalesNTM',
          format: METRIC_FORMATS.MULTIPLE,
          proMetric: false,
          views: {
            compSheet: true,
            valuation: {
              axis: 'y',
            },
          },
          metadata: {
            displayGraph: true,
            displayScatter: true,
          },
        },
      },
    },

    evGmNtm: {
      id: 'evGmNtm',
      field: 'EV / GM NTM',
      group: METRIC_GROUPS.COMPANY_STATS,
      metricType: METRIC_TYPES.INSTANT,
      variants: {
        [VARIANT_TYPES.MULTIPLE]: {
          field: 'EV/GM NTM',
          headerName: 'EV/GM',
          sublabel: 'NTM',
          dbField: 'evToGrossMarginNTM',
          format: METRIC_FORMATS.MULTIPLE,
          proMetric: false,
          views: {
            compSheet: true,
            valuation: {
              axis: 'y',
            },
          },
          metadata: {
            displayGraph: true,
            displayScatter: true,
          },
        },
      },
    },

    evSalesTtm: {
      id: 'evSalesTtm',
      field: 'EV / Sales TTM',
      group: METRIC_GROUPS.COMPANY_STATS,
      metricType: METRIC_TYPES.INSTANT,
      variants: {
        [VARIANT_TYPES.MULTIPLE]: {
          field: 'EV/Sales TTM',
          headerName: 'EV/Sales',
          sublabel: 'TTM',
          dbField: 'evToSalesTTM',
          format: METRIC_FORMATS.MULTIPLE,
          proMetric: false,
          views: {
            compSheet: true,
            valuation: {
              axis: 'y',
            },
          },
          metadata: {
            displayGraph: true,
            displayScatter: true,
          },
        },
      },
    },

    evGmTtm: {
      id: 'evGmTtm',
      field: 'EV / GM TTM',
      group: METRIC_GROUPS.COMPANY_STATS,
      metricType: METRIC_TYPES.INSTANT,
      variants: {
        [VARIANT_TYPES.MULTIPLE]: {
          field: 'EV/GM TTM',
          headerName: 'EV/GM',
          sublabel: 'TTM',
          dbField: 'evToGrossMarginTTM',
          format: METRIC_FORMATS.MULTIPLE,
          proMetric: false,
          views: {
            compSheet: true,
            valuation: {
              axis: 'y',
            },
          },
          metadata: {
            displayGraph: true,
            displayScatter: true,
          },
        },
      },
    },

    priceFcfTtm: {
      id: 'priceFcfTtm',
      field: 'Price / FCF TTM',
      group: METRIC_GROUPS.COMPANY_STATS,
      metricType: METRIC_TYPES.INSTANT,
      variants: {
        [VARIANT_TYPES.MULTIPLE]: {
          field: 'Price/FCF TTM',
          headerName: 'Price/FCF',
          sublabel: 'TTM',
          dbField: 'priceToFcfTTM',
          format: METRIC_FORMATS.MULTIPLE,
          proMetric: false,
          views: {
            compSheet: true,
            valuation: {
              axis: 'y',
            },
          },
          metadata: {
            displayGraph: true,
            displayScatter: true,
          },
        },
      },
    },

    priceFcfNtm: {
      id: 'priceFcfNtm',
      field: 'Price / FCF NTM',
      group: METRIC_GROUPS.COMPANY_STATS,
      metricType: METRIC_TYPES.INSTANT,
      variants: {
        [VARIANT_TYPES.MULTIPLE]: {
          field: 'Price/FCF NTM',
          headerName: 'Price/FCF',
          sublabel: 'NTM',
          dbField: 'priceToFcfNTM',
          format: METRIC_FORMATS.MULTIPLE,
          proMetric: false,
          views: {
            compSheet: true,
            valuation: {
              axis: 'y',
            },
          },
          metadata: {
            displayGraph: true,
            displayScatter: true,
          },
        },
      },
    },


  // ========== COMPANY STATISTICS ==========

    marketCap: {
      id: 'marketCap',
      field: 'Market Cap',
      group: METRIC_GROUPS.COMPANY_STATS,
      metricType: METRIC_TYPES.INSTANT,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'Market Cap',
          dbField: 'marketCap',
          format: METRIC_FORMATS.CURRENCY,
          proMetric: false,
          views: {
            compSheet: true,
            securityAnalysis: true,
          },
          metadata: {},
        },
      },
    },

    enterpriseValue: {
      id: 'enterpriseValue',
      field: 'Enterprise Value',
      group: METRIC_GROUPS.COMPANY_STATS,
      metricType: METRIC_TYPES.INSTANT,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'Enterprise Value',
          dbField: 'enterpriseValue',
          format: METRIC_FORMATS.CURRENCY,
          proMetric: false,
          views: {
            compSheet: true,
            securityAnalysis: true,
          },
          metadata: {},
        },
      },
    },

    employees: {
      id: 'employees',
      field: 'Employees',
      group: METRIC_GROUPS.COMPANY_STATS,
      metricType: METRIC_TYPES.INSTANT,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'Employees',
          dbField: 'employees',
          format: METRIC_FORMATS.NUMBER,
          proMetric: false,
          views: {
            compSheet: true,
            securityAnalysis: true,
          },
          metadata: {},
        },
      },
    },

    shareCount: {
      id: 'shareCount',
      field: 'Share Count',
      group: METRIC_GROUPS.PROFITABILITY,
      metricType: METRIC_TYPES.INSTANT,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'Shares Outstanding',
          dbField: 'sharesOutstanding',
          format: METRIC_FORMATS.NUMBER,
          proMetric: false,
          views: {
            securityAnalysis: true,
          },
          metadata: {
            displayScale: 'M',
            displayFormat: 'millions',
          },
        },
        [VARIANT_TYPES.GROWTH]: {
          field: 'Share Count YOY%',
          headerName: 'Share Count',
          sublabel: 'YoY%',
          dbField: 'shareCountyoy',
          format: METRIC_FORMATS.PERCENTAGE,
          proMetric: true,
          views: {
            compSheet: true,
            dashboard: true,
            valuation: {
              axis: 'x',
            },
          },
          metadata: {
            displayGraph: true,
          },
        },
      },
    },

    yearFounded: {
      id: 'yearFounded',
      field: 'Year Founded',
      group: METRIC_GROUPS.COMPANY_STATS,
      metricType: METRIC_TYPES.INSTANT,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'Year Founded',
          dbField: 'yearFounded',
          format: METRIC_FORMATS.NUMBER,
          proMetric: false,
          views: {
            compSheet: true,
            securityAnalysis: true,
          },
          metadata: {},
        },
      },
    },

    accumulatedAmortization: {
      id: 'accumulatedAmortization',
      field: 'Accumulated Amortization',
      group: METRIC_GROUPS.COMPANY_STATS,
      metricType: METRIC_TYPES.INSTANT,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'Accumulated Amortization',
          dbField: 'accumulatedAmortization',
          format: METRIC_FORMATS.CURRENCY,
          proMetric: false,
          views: {
            securityAnalysis: true,
          },
          metadata: {
            fontWeight: 400,
            tab: 1,
          },
        },
      },
    },

    accumulatedDepreciation: {
      id: 'accumulatedDepreciation',
      field: 'Accumulated Depreciation',
      group: METRIC_GROUPS.COMPANY_STATS,
      metricType: METRIC_TYPES.INSTANT,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'Accumulated Depreciation',
          dbField: 'accumulatedDepreciation',
          format: METRIC_FORMATS.CURRENCY,
          proMetric: false,
          views: {
            securityAnalysis: true,
          },
          metadata: {
            fontWeight: 400,
            tab: 1,
          },
        },
      },
    },

    accumulatedOtherComprehensiveIncome: {
      id: 'accumulatedOtherComprehensiveIncome',
      field: 'Accumulated Other Comprehensive Income',
      group: METRIC_GROUPS.COMPANY_STATS,
      metricType: METRIC_TYPES.INSTANT,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'AOCI',
          dbField: 'accumulatedOtherComprehensiveIncome',
          format: METRIC_FORMATS.CURRENCY,
          proMetric: false,
          views: {
            securityAnalysis: true,
          },
          metadata: {
            fontWeight: 400,
            tab: 1,
          },
        },
      },
    },

    acquisitionsCash: {
      id: 'acquisitionsCash',
      field: 'Acquisitions',
      group: METRIC_GROUPS.PROFITABILITY,
      metricType: METRIC_TYPES.FLOW,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'Acquisitions',
          dbField: 'acquisitionsCash',
          format: METRIC_FORMATS.CURRENCY,
          proMetric: false,
          views: {
            securityAnalysis: true,
          },
          metadata: {
            fontWeight: 400,
            tab: 1,
          },
        },
      },
    },

    additionalPaidInCapital: {
      id: 'additionalPaidInCapital',
      field: 'Additional Paid-In Capital',
      group: METRIC_GROUPS.COMPANY_STATS,
      metricType: METRIC_TYPES.INSTANT,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'Additional Paid-In Capital',
          dbField: 'additionalPaidInCapital',
          format: METRIC_FORMATS.CURRENCY,
          proMetric: false,
          views: {
            securityAnalysis: true,
          },
          metadata: {
            fontWeight: 400,
            tab: 1,
          },
        },
      },
    },

    amortizationOfIntangibles: {
      id: 'amortizationOfIntangibles',
      field: 'Amortization of Intangibles',
      group: METRIC_GROUPS.PROFITABILITY,
      metricType: METRIC_TYPES.FLOW,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'Amortization of Intangibles',
          dbField: 'amortizationOfIntangibles',
          format: METRIC_FORMATS.CURRENCY,
          proMetric: false,
          views: {
            securityAnalysis: true,
          },
          metadata: {
            fontWeight: 400,
            tab: 1,
          },
        },
      },
    },

    capitalExpenditures: {
      id: 'capitalExpenditures',
      field: 'Capital Expenditures (EDGAR)',
      group: METRIC_GROUPS.PROFITABILITY,
      metricType: METRIC_TYPES.FLOW,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'Capital Expenditures',
          dbField: 'capitalExpenditures',
          format: METRIC_FORMATS.CURRENCY,
          proMetric: false,
          views: {
            securityAnalysis: true,
          },
          metadata: {
            fontWeight: 400,
            tab: 1,
          },
        },
      },
    },

    capitalizedCommissions: {
      id: 'capitalizedCommissions',
      field: 'Capitalized Commissions',
      group: METRIC_GROUPS.TOP_LINE,
      metricType: METRIC_TYPES.FLOW,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'Capitalized Commissions',
          dbField: 'capitalizedCommissions',
          format: METRIC_FORMATS.CURRENCY,
          proMetric: false,
          views: {
            securityAnalysis: true,
          },
          metadata: {
            fontWeight: 500,
            tab: 1,
          },
        },
        [VARIANT_TYPES.GROWTH]: {
          field: 'Capitalized Commissions YOY%',
          headerName: 'Cap. Commissions',
          sublabel: 'YoY%',
          dbField: 'capitalizedCommissionsyoy',
          format: METRIC_FORMATS.PERCENTAGE,
          proMetric: true,
          views: {
            compSheet: true,
          },
          metadata: {
            displayGraph: true,
          },
        },
        [VARIANT_TYPES.TTM_GROWTH]: {
          field: 'TTM Capitalized Commissions YOY%',
          headerName: 'Cap. Commissions',
          sublabel: 'TTM YoY%',
          dbField: 'capitalizedCommissionsttmYoy',
          format: METRIC_FORMATS.PERCENTAGE,
          proMetric: true,
          views: {
            compSheet: true,
          },
          metadata: {
            displayGraph: true,
          },
        },
      },
    },

    cashFlowFromFinancing: {
      id: 'cashFlowFromFinancing',
      field: 'Financing Cash Flow',
      group: METRIC_GROUPS.PROFITABILITY,
      metricType: METRIC_TYPES.FLOW,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'Financing Cash Flow',
          dbField: 'cashFlowFromFinancing',
          format: METRIC_FORMATS.CURRENCY,
          proMetric: false,
          views: {
            securityAnalysis: true,
          },
          metadata: {
            fontWeight: 400,
            tab: 1,
            calc: ['% of Revenue'],
          },
        },
      },
    },

    cashFlowFromInvesting: {
      id: 'cashFlowFromInvesting',
      field: 'Investing Cash Flow',
      group: METRIC_GROUPS.PROFITABILITY,
      metricType: METRIC_TYPES.FLOW,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'Investing Cash Flow',
          dbField: 'cashFlowFromInvesting',
          format: METRIC_FORMATS.CURRENCY,
          proMetric: false,
          views: {
            securityAnalysis: true,
          },
          metadata: {
            fontWeight: 400,
            tab: 1,
            calc: ['% of Revenue'],
          },
        },
      },
    },

    comprehensiveIncome: {
      id: 'comprehensiveIncome',
      field: 'Comprehensive Income',
      group: METRIC_GROUPS.PROFITABILITY,
      metricType: METRIC_TYPES.FLOW,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'Comprehensive Income',
          dbField: 'comprehensiveIncome',
          format: METRIC_FORMATS.CURRENCY,
          proMetric: false,
          views: {
            securityAnalysis: true,
          },
          metadata: {
            fontWeight: 400,
            tab: 1,
          },
        },
      },
    },

    convertibleDebt: {
      id: 'convertibleDebt',
      field: 'Convertible Debt',
      group: METRIC_GROUPS.COMPANY_STATS,
      metricType: METRIC_TYPES.INSTANT,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'Convertible Debt',
          dbField: 'convertibleDebt',
          format: METRIC_FORMATS.CURRENCY,
          proMetric: false,
          views: {
            securityAnalysis: true,
          },
          metadata: {
            fontWeight: 400,
            tab: 1,
          },
        },
      },
    },

    deferredCommissions: {
      id: 'deferredCommissions',
      field: 'Deferred Sales Commissions',
      group: METRIC_GROUPS.TOP_LINE,
      metricType: METRIC_TYPES.INSTANT,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'Deferred Commissions',
          dbField: 'deferredCommissions',
          format: METRIC_FORMATS.CURRENCY,
          proMetric: false,
          views: {
            securityAnalysis: true,
          },
          metadata: {
            fontWeight: 500,
            tab: 1,
          },
        },
        [VARIANT_TYPES.GROWTH]: {
          field: 'Deferred Commissions YOY%',
          headerName: 'Def. Commissions',
          sublabel: 'YoY%',
          dbField: 'deferredCommissionsyoy',
          format: METRIC_FORMATS.PERCENTAGE,
          proMetric: true,
          views: {
            compSheet: true,
          },
          metadata: {
            displayGraph: true,
          },
        },
      },
    },

    deferredCommissionsAmortization: {
      id: 'deferredCommissionsAmortization',
      field: 'Deferred Commissions Amortization',
      group: METRIC_GROUPS.COMPANY_STATS,
      metricType: METRIC_TYPES.FLOW,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'Deferred Commissions Amortization',
          dbField: 'deferredCommissionsAmortization',
          format: METRIC_FORMATS.CURRENCY,
          proMetric: false,
          views: {
            securityAnalysis: true,
          },
          metadata: {
            fontWeight: 400,
            tab: 1,
          },
        },
      },
    },

    deferredCommissionsCurrent: {
      id: 'deferredCommissionsCurrent',
      field: 'Deferred Commissions (Current)',
      group: METRIC_GROUPS.COMPANY_STATS,
      metricType: METRIC_TYPES.INSTANT,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'Deferred Commissions (Current)',
          dbField: 'deferredCommissionsCurrent',
          format: METRIC_FORMATS.CURRENCY,
          proMetric: false,
          views: {
            securityAnalysis: true,
          },
          metadata: {
            fontWeight: 400,
            tab: 2,
          },
        },
      },
    },

    deferredCommissionsNonCurrent: {
      id: 'deferredCommissionsNonCurrent',
      field: 'Deferred Commissions (Non-Current)',
      group: METRIC_GROUPS.COMPANY_STATS,
      metricType: METRIC_TYPES.INSTANT,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'Deferred Commissions (Non-Current)',
          dbField: 'deferredCommissionsNonCurrent',
          format: METRIC_FORMATS.CURRENCY,
          proMetric: false,
          views: {
            securityAnalysis: true,
          },
          metadata: {
            fontWeight: 400,
            tab: 2,
          },
        },
      },
    },

    deferredTaxAssets: {
      id: 'deferredTaxAssets',
      field: 'Deferred Tax Assets',
      group: METRIC_GROUPS.COMPANY_STATS,
      metricType: METRIC_TYPES.INSTANT,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'Deferred Tax Assets',
          dbField: 'deferredTaxAssets',
          format: METRIC_FORMATS.CURRENCY,
          proMetric: false,
          views: {
            securityAnalysis: true,
          },
          metadata: {
            fontWeight: 400,
            tab: 1,
          },
        },
      },
    },

    depreciation: {
      id: 'depreciation',
      field: 'Depreciation',
      group: METRIC_GROUPS.PROFITABILITY,
      metricType: METRIC_TYPES.FLOW,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'Depreciation',
          dbField: 'depreciation',
          format: METRIC_FORMATS.CURRENCY,
          proMetric: false,
          views: {
            securityAnalysis: true,
          },
          metadata: {
            fontWeight: 400,
            tab: 1,
          },
        },
      },
    },

    depreciationAndAmortization: {
      id: 'depreciationAndAmortization',
      field: 'Depreciation & Amortization',
      group: METRIC_GROUPS.PROFITABILITY,
      metricType: METRIC_TYPES.FLOW,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'Depreciation & Amortization',
          dbField: 'depreciationAndAmortization',
          format: METRIC_FORMATS.CURRENCY,
          proMetric: false,
          views: {
            securityAnalysis: true,
          },
          metadata: {
            fontWeight: 400,
            tab: 1,
            calc: ['% of Revenue'],
          },
        },
      },
    },

    employeeRelatedLiabilities: {
      id: 'employeeRelatedLiabilities',
      field: 'Employee-Related Liabilities',
      group: METRIC_GROUPS.COMPANY_STATS,
      metricType: METRIC_TYPES.INSTANT,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'Employee-Related Liabilities',
          dbField: 'employeeRelatedLiabilities',
          format: METRIC_FORMATS.CURRENCY,
          proMetric: false,
          views: {
            securityAnalysis: true,
          },
          metadata: {
            fontWeight: 400,
            tab: 1,
          },
        },
      },
    },

    fxEffect: {
      id: 'fxEffect',
      field: 'FX Effect on Cash',
      group: METRIC_GROUPS.PROFITABILITY,
      metricType: METRIC_TYPES.FLOW,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'FX Effect on Cash',
          dbField: 'fxEffect',
          format: METRIC_FORMATS.CURRENCY,
          proMetric: false,
          views: {
            securityAnalysis: true,
          },
          metadata: {
            fontWeight: 400,
            tab: 1,
          },
        },
      },
    },

    hc: {
      id: 'hc',
      field: 'Headcount',
      group: METRIC_GROUPS.COMPANY_STATS,
      metricType: METRIC_TYPES.INSTANT,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'Headcount',
          dbField: 'hc',
          format: METRIC_FORMATS.NUMBER,
          proMetric: false,
          views: {
            securityAnalysis: true,
          },
          metadata: {
            fontWeight: 400,
            tab: 1,
          },
        },
      },
    },

    hcCogs: {
      id: 'hcCogs',
      field: 'Headcount (COGS)',
      group: METRIC_GROUPS.COMPANY_STATS,
      metricType: METRIC_TYPES.INSTANT,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'Headcount (COGS)',
          dbField: 'hcCogs',
          format: METRIC_FORMATS.NUMBER,
          proMetric: false,
          views: {
            securityAnalysis: true,
          },
          metadata: {
            fontWeight: 400,
            tab: 2,
          },
        },
      },
    },

    hcCogsOther: {
      id: 'hcCogsOther',
      field: 'Headcount (Other COGS)',
      group: METRIC_GROUPS.COMPANY_STATS,
      metricType: METRIC_TYPES.INSTANT,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'Headcount (Other COGS)',
          dbField: 'hcCogsOther',
          format: METRIC_FORMATS.NUMBER,
          proMetric: false,
          views: {
            securityAnalysis: true,
          },
          metadata: {
            fontWeight: 400,
            tab: 2,
          },
        },
      },
    },

    hcGA: {
      id: 'hcGA',
      field: 'Headcount (G&A)',
      group: METRIC_GROUPS.COMPANY_STATS,
      metricType: METRIC_TYPES.INSTANT,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'Headcount (G&A)',
          dbField: 'hcGA',
          format: METRIC_FORMATS.NUMBER,
          proMetric: false,
          views: {
            securityAnalysis: true,
          },
          metadata: {
            fontWeight: 400,
            tab: 2,
          },
        },
      },
    },

    hcInt: {
      id: 'hcInt',
      field: 'Headcount (International)',
      group: METRIC_GROUPS.COMPANY_STATS,
      metricType: METRIC_TYPES.INSTANT,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'Headcount (International)',
          dbField: 'hcInt',
          format: METRIC_FORMATS.NUMBER,
          proMetric: false,
          views: {
            securityAnalysis: true,
          },
          metadata: {
            fontWeight: 400,
            tab: 2,
          },
        },
      },
    },

    hcRD: {
      id: 'hcRD',
      field: 'Headcount (R&D)',
      group: METRIC_GROUPS.COMPANY_STATS,
      metricType: METRIC_TYPES.INSTANT,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'Headcount (R&D)',
          dbField: 'hcRD',
          format: METRIC_FORMATS.NUMBER,
          proMetric: false,
          views: {
            securityAnalysis: true,
          },
          metadata: {
            fontWeight: 400,
            tab: 2,
          },
        },
      },
    },

    hcSM: {
      id: 'hcSM',
      field: 'Headcount (S&M)',
      group: METRIC_GROUPS.COMPANY_STATS,
      metricType: METRIC_TYPES.INSTANT,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'Headcount (S&M)',
          dbField: 'hcSM',
          format: METRIC_FORMATS.NUMBER,
          proMetric: false,
          views: {
            securityAnalysis: true,
          },
          metadata: {
            fontWeight: 400,
            tab: 2,
          },
        },
      },
    },

    hcSvc: {
      id: 'hcSvc',
      field: 'Headcount (Professional Services)',
      group: METRIC_GROUPS.COMPANY_STATS,
      metricType: METRIC_TYPES.INSTANT,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'Headcount (Professional Services)',
          dbField: 'hcSvc',
          format: METRIC_FORMATS.NUMBER,
          proMetric: false,
          views: {
            securityAnalysis: true,
          },
          metadata: {
            fontWeight: 400,
            tab: 2,
          },
        },
      },
    },

    hcUS: {
      id: 'hcUS',
      field: 'Headcount (US)',
      group: METRIC_GROUPS.COMPANY_STATS,
      metricType: METRIC_TYPES.INSTANT,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'Headcount (US)',
          dbField: 'hcUS',
          format: METRIC_FORMATS.NUMBER,
          proMetric: false,
          views: {
            securityAnalysis: true,
          },
          metadata: {
            fontWeight: 400,
            tab: 2,
          },
        },
      },
    },

    intangibleAssetsGross: {
      id: 'intangibleAssetsGross',
      field: 'Intangible Assets Gross',
      group: METRIC_GROUPS.COMPANY_STATS,
      metricType: METRIC_TYPES.INSTANT,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'Intangible Assets Gross',
          dbField: 'intangibleAssetsGross',
          format: METRIC_FORMATS.CURRENCY,
          proMetric: false,
          views: {
            securityAnalysis: true,
          },
          metadata: {
            fontWeight: 400,
            tab: 1,
          },
        },
      },
    },

    nonOperatingIncome: {
      id: 'nonOperatingIncome',
      field: 'Non-Operating Income',
      group: METRIC_GROUPS.PROFITABILITY,
      metricType: METRIC_TYPES.FLOW,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'Non-Operating Income',
          dbField: 'nonOperatingIncome',
          format: METRIC_FORMATS.CURRENCY,
          proMetric: false,
          views: {
            securityAnalysis: true,
          },
          metadata: {
            fontWeight: 400,
            tab: 1,
            calc: ['% of Revenue'],
          },
        },
      },
    },

    operatingLeaseLiability: {
      id: 'operatingLeaseLiability',
      field: 'Operating Lease Liabilities',
      group: METRIC_GROUPS.COMPANY_STATS,
      metricType: METRIC_TYPES.INSTANT,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'Operating Lease Liabilities',
          dbField: 'operatingLeaseLiability',
          format: METRIC_FORMATS.CURRENCY,
          proMetric: false,
          views: {
            securityAnalysis: true,
          },
          metadata: {
            fontWeight: 400,
            tab: 1,
          },
        },
      },
    },

    operatingLeaseLiabilityCurrent: {
      id: 'operatingLeaseLiabilityCurrent',
      field: 'Operating Lease Liabilities (Current)',
      group: METRIC_GROUPS.COMPANY_STATS,
      metricType: METRIC_TYPES.INSTANT,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'Operating Lease Liabilities (Current)',
          dbField: 'operatingLeaseLiabilityCurrent',
          format: METRIC_FORMATS.CURRENCY,
          proMetric: false,
          views: {
            securityAnalysis: true,
          },
          metadata: {
            fontWeight: 400,
            tab: 1,
          },
        },
      },
    },

    operatingLeaseROU: {
      id: 'operatingLeaseROU',
      field: 'Operating Lease ROU Assets',
      group: METRIC_GROUPS.COMPANY_STATS,
      metricType: METRIC_TYPES.INSTANT,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'ROU Assets',
          dbField: 'operatingLeaseROU',
          format: METRIC_FORMATS.CURRENCY,
          proMetric: false,
          views: {
            securityAnalysis: true,
          },
          metadata: {
            fontWeight: 400,
            tab: 1,
          },
        },
      },
    },

    otherAssetsNoncurrent: {
      id: 'otherAssetsNoncurrent',
      field: 'Other Non-Current Assets',
      group: METRIC_GROUPS.COMPANY_STATS,
      metricType: METRIC_TYPES.INSTANT,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'Other Non-Current Assets',
          dbField: 'otherAssetsNoncurrent',
          format: METRIC_FORMATS.CURRENCY,
          proMetric: false,
          views: {
            securityAnalysis: true,
          },
          metadata: {
            fontWeight: 400,
            tab: 1,
          },
        },
      },
    },

    otherComprehensiveIncome: {
      id: 'otherComprehensiveIncome',
      field: 'Other Comprehensive Income',
      group: METRIC_GROUPS.PROFITABILITY,
      metricType: METRIC_TYPES.FLOW,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'Other Comprehensive Income',
          dbField: 'otherComprehensiveIncome',
          format: METRIC_FORMATS.CURRENCY,
          proMetric: false,
          views: {
            securityAnalysis: true,
          },
          metadata: {
            fontWeight: 400,
            tab: 1,
          },
        },
      },
    },

    otherLiabilitiesNoncurrent: {
      id: 'otherLiabilitiesNoncurrent',
      field: 'Other Non-Current Liabilities',
      group: METRIC_GROUPS.COMPANY_STATS,
      metricType: METRIC_TYPES.INSTANT,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'Other Non-Current Liabilities',
          dbField: 'otherLiabilitiesNoncurrent',
          format: METRIC_FORMATS.CURRENCY,
          proMetric: false,
          views: {
            securityAnalysis: true,
          },
          metadata: {
            fontWeight: 400,
            tab: 1,
          },
        },
      },
    },

    prepaidExpenses: {
      id: 'prepaidExpenses',
      field: 'Prepaid Expenses & Other Current Assets',
      group: METRIC_GROUPS.COMPANY_STATS,
      metricType: METRIC_TYPES.INSTANT,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'Prepaid & Other Current',
          dbField: 'prepaidExpenses',
          format: METRIC_FORMATS.CURRENCY,
          proMetric: false,
          views: {
            securityAnalysis: true,
          },
          metadata: {
            fontWeight: 400,
            tab: 1,
          },
        },
      },
    },

    pretaxIncome: {
      id: 'pretaxIncome',
      field: 'Pre-Tax Income',
      group: METRIC_GROUPS.PROFITABILITY,
      metricType: METRIC_TYPES.FLOW,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'Pre-Tax Income',
          dbField: 'pretaxIncome',
          format: METRIC_FORMATS.CURRENCY,
          proMetric: false,
          views: {
            securityAnalysis: true,
          },
          metadata: {
            fontWeight: 400,
            tab: 1,
            calc: ['% of Revenue'],
          },
        },
      },
    },

    propertyPlantEquipmentGross: {
      id: 'propertyPlantEquipmentGross',
      field: 'PP&E Gross',
      group: METRIC_GROUPS.COMPANY_STATS,
      metricType: METRIC_TYPES.INSTANT,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'PP&E Gross',
          dbField: 'propertyPlantEquipmentGross',
          format: METRIC_FORMATS.CURRENCY,
          proMetric: false,
          views: {
            securityAnalysis: true,
          },
          metadata: {
            fontWeight: 400,
            tab: 1,
          },
        },
      },
    },

    restructuringCharges: {
      id: 'restructuringCharges',
      field: 'Restructuring Charges',
      group: METRIC_GROUPS.PROFITABILITY,
      metricType: METRIC_TYPES.FLOW,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'Restructuring Charges',
          dbField: 'restructuringCharges',
          format: METRIC_FORMATS.CURRENCY,
          proMetric: false,
          views: {
            securityAnalysis: true,
          },
          metadata: {
            fontWeight: 400,
            tab: 1,
            calc: ['% of Revenue'],
          },
        },
      },
    },

    retainedEarnings: {
      id: 'retainedEarnings',
      field: 'Retained Earnings',
      group: METRIC_GROUPS.COMPANY_STATS,
      metricType: METRIC_TYPES.INSTANT,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'Retained Earnings',
          dbField: 'retainedEarnings',
          format: METRIC_FORMATS.CURRENCY,
          proMetric: false,
          views: {
            securityAnalysis: true,
          },
          metadata: {
            fontWeight: 400,
            tab: 1,
          },
        },
      },
    },

    totalOperatingExpenses: {
      id: 'totalOperatingExpenses',
      field: 'Total Operating Expenses',
      group: METRIC_GROUPS.PROFITABILITY,
      metricType: METRIC_TYPES.FLOW,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'Total Operating Expenses',
          dbField: 'totalOperatingExpenses',
          format: METRIC_FORMATS.CURRENCY,
          proMetric: false,
          views: {
            securityAnalysis: true,
          },
          metadata: {
            fontWeight: 500,
            tab: 1,
          },
        },
      },
    },

    treasuryStock: {
      id: 'treasuryStock',
      field: 'Treasury Stock',
      group: METRIC_GROUPS.COMPANY_STATS,
      metricType: METRIC_TYPES.INSTANT,
      variants: {
        [VARIANT_TYPES.ABSOLUTE]: {
          field: 'Treasury Stock',
          dbField: 'treasuryStock',
          format: METRIC_FORMATS.CURRENCY,
          proMetric: false,
          views: {
            securityAnalysis: true,
          },
          metadata: {
            fontWeight: 400,
            tab: 1,
          },
        },
      },
    },

};
